"use client";

import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";
import { SuccessMessageType } from "@/interface/types";

const FormPlaceholder = () => (
  <div className="flex flex-col gap-3 min-h-[300px] animate-pulse">
    <div className="h-11 w-full rounded-lg bg-[#EDF1F7]" />
    <div className="h-11 w-full rounded-lg bg-[#EDF1F7]" />
    <div className="h-11 w-full rounded-lg bg-[#EDF1F7]" />
    <div className="h-4 w-3/4 rounded bg-[#EDF1F7] mt-1" />
    <div className="h-11 w-full rounded-lg bg-[#DCE7F5] mt-2" />
  </div>
);

const EnqueryForm = dynamic(() => import("@/components/forms/EnqueryForm"), {
  ssr: false,
  loading: () => <FormPlaceholder />,
});

/* Number of questions shown before the reader taps “See More”. The rest are
   rendered but hidden with a class, so every answer still ships in the HTML
   for crawlers instead of appearing only after a click. */
const VISIBLE_COUNT = 3;

const faqs = [
  {
    question: "What is CMA course?",
    answer:
      "The CMA course (Certified Management Accountant) is a two-part certification from the Institute of Management Accountants (IMA), USA, covering financial planning, performance, analytics, and strategic financial management.",
  },
  {
    question: "What is the CMA course full form?",
    answer: "CMA stands for Certified Management Accountant.",
  },
  {
    question: "How long does the CMA course take to complete?",
    answer:
      "Most candidates finish both parts in 8-12 months, though IMA allows up to 3 years from registration to complete both parts.",
  },
  {
    question: "What are the CMA course fees?",
    answer:
      "The total investment includes IMA membership, an entrance fee, and exam fees for both parts, plus training and study material costs. See the breakdown above for exact figures through Miles’ IMA Diamond Partner discount.",
  },
  {
    question: "How are US CMA course fees different from Indian CMA fees?",
    answer:
      "The US credential is priced in dollars and paid largely to IMA, USA, covering membership, entrance, and two exam parts, while the ICMAI qualification is billed in rupees across three levels. Use the table above to compare the full outlay.",
  },
  {
    question: "What are the CMA subjects, and what does the course cover?",
    answer:
      "There are two parts: Part 1 (Financial Planning, Performance & Analytics, including Technology and Analytics) and Part 2 (Strategic Financial Management), with six sections each.",
  },
  {
    question: "Is the US CMA the same as the Indian CMA?",
    answer: (
      <>
        No. The{" "}
        <a
          href="https://www.mileseducation.com/cma"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#39F] underline"
        >
          US CMA
        </a>{" "}
        is issued by IMA, USA, and is a two-part, globally recognized
        certification. The Indian CMA is a separate, three-level qualification
        from ICMAI. If you&rsquo;re targeting MNCs, GCCs, or a global career,
        the US CMA is generally the stronger fit.
      </>
    ),
  },
  {
    question: "Has the CMA exam format changed for 2026?",
    answer:
      "Yes. The 100-question MCQ section is unchanged, but IMA has retired the essay section and replaced it with Case-Based Questions (CBQs). The May–June 2026 window was the last one where candidates could choose essays; from the September–October 2026 window onward, CBQs are the only format on English-language CMA exams in the Americas, Europe, the Middle East, Asia-Pacific, and Africa. China, Japan, and Taiwan continue to offer essays. The syllabus, topic weightage, exam length, passing score, and fees are all unchanged.",
  },
  {
    question:
      "Do I need a full-time job to meet the CMA experience requirement?",
    answer:
      "No. Continuous part-time work of 20 or more hours a week also qualifies, counted at two years of part-time work for one year of the required experience. The two-year requirement can be completed before you start or within seven years of passing the exam.",
  },
  {
    question: "Is there a CMA online course option?",
    answer:
      "Yes, Miles offers a full CMA online course and CMA online coaching path, alongside classroom options in 9 Indian cities.",
  },
  {
    question: "How do I get CMA admission?",
    answer:
      "Talk to a Miles counsellor, register as an IMA member, start structured preparation, and choose your Prometric testing window. See the step-by-step process above.",
  },
  {
    question: "Which is the best CMA institute in India?",
    answer: (
      <>
        That depends on your priorities, but Miles Education is India&rsquo;s
        largest CMA training provider by alumni base, with IMA Diamond Partner
        status, renowned faculty like Varun Jain, and an integrated AI-ready
        learning experience through{" "}
        <a
          href="https://www.mileseducation.com/caira"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[#39F] underline"
        >
          CAIRA
        </a>{" "}
        (Certified AI-Ready Accountant).
      </>
    ),
  },
];

const CmaCourseFaq = () => {
  const [showAll, setShowAll] = useState(false);
  const [mountForm, setMountForm] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  /* The form sits far below the fold, so its bundle is only fetched once the
     card is about to scroll into view — nothing extra runs on first load. */
  useEffect(() => {
    const node = formRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setMountForm(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setMountForm(true);
          observer.disconnect();
        }
      },
      { rootMargin: "400px 0px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <section className="w-full bg-white md:py-[50px] max-md:bg-[#F3F3F3]">
      <div className="main_container">
        <div className="flex flex-col md:flex-row md:items-start md:gap-7 gap-3">
          {/* Talk to an Expert — first on mobile, sticky right rail on desktop.
              `md:self-start` keeps the column from stretching to the row height,
              which is what lets the card stick; `top` clears the pinned header
              and it releases naturally at the end of the section. */}
          <div className="order-1 md:order-2 md:w-[404px] md:shrink-0 md:self-start md:sticky md:top-[130px]">
            <div
              ref={formRef}
              className="bg-white rounded-2xl border border-[#E6E6E6] md:p-4 p-4"
            >
              <h3 className="text-[24px] font-bold text-center leading-[1.3] md:mb-4 mb-3">
                <span className="text-[#333]">Talk to an </span>
                <span className="text-[#39F]">Expert</span>
              </h3>
              {mountForm ? (
                <EnqueryForm
                  pageName="CMA"
                  courseId={2}
                  darkBackground={false}
                  submitButtonName="Request a Callback"
                  formType="Talk to Expert Form"
                  isAStepperForm
                  successMessage={SuccessMessageType.ACCOUNTING}
                />
              ) : (
                <FormPlaceholder />
              )}
            </div>
          </div>

          {/* Questions */}
          <div className="order-2 md:order-1 md:flex-1 md:min-w-0 max-md:bg-white max-md:rounded-2xl max-md:px-4 py-4 mb-4">
            <h2 className="md:text-[32px] text-[24px] font-bold text-center leading-[1.2] text-[#333] md:mb-6 mb-3">
              FAQs
            </h2>

            <div className="flex flex-col md:gap-3.5 gap-3">
              {faqs?.map((faq, idx) => (
                <div
                  key={faq.question}
                  className={`rounded-[16px] bg-[#F7FBFF] md:p-4 p-4 ${
                    !showAll && idx >= VISIBLE_COUNT ? "hidden" : ""
                  }`}
                >
                  <h3 className="text-[#333] md:text-[16px] text-[14px] font-bold leading-normal">
                    {faq.question}
                  </h3>
                  <p className="text-[#999] md:text-[14px] text-[12px] font-normal leading-normal md:mt-2.5 mt-2">
                    {faq.answer}
                  </p>
                </div>
              ))}
            </div>

            {faqs.length > VISIBLE_COUNT && (
              <div className="flex justify-center md:mt-6 mt-4">
                <button
                  type="button"
                  onClick={() => setShowAll((current) => !current)}
                  aria-expanded={showAll}
                  className="inline-flex items-center gap-1 text-[#39F] md:text-[13px] text-[11px] font-bold"
                >
                  {showAll ? "See Less" : "See More"}
                  <span aria-hidden="true">{showAll ? "↑" : "↓"}</span>
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CmaCourseFaq;
